"use client";
import React from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Link from "next/link";
import PageTransition from "@/components/animations/PageTransition";
import FadeInView from "@/components/animations/FadeInView";
import StaggerContainer from "@/components/animations/StaggerContainer";
import Breadcrumb from "@/components/Breadcrumb";

const challenges = [
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
    title: "Multi-Site Connectivity",
    desc: "Head offices, project sites and sales suites all need reliable, consistent network access.",
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="12" y1="18" x2="12" y2="12" />
        <line x1="9" y1="15" x2="15" y2="15" />
      </svg>
    ),
    title: "Secure Document Sharing",
    desc: "Plans, contracts and compliance documents need to be shared securely between teams and stakeholders.",
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </svg>
    ),
    title: "Remote Access & VPN",
    desc: "Remote teams need reliable VPN and cloud access from any location — site, office or home.",
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
    title: "Fast Site Setup",
    desc: "Temporary site offices and show homes need rapid IT deployment that can be relocated.",
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
    title: "Data Privacy Compliance",
    desc: "Client records and financial data require privacy compliance and secure storage.",
  },
];

const services = [
  { title: "Multi-Site Networking & SD-WAN", desc: "Connect head offices, development sites and sales suites with reliable, managed networking.", href: "/network-solutions" },
  { title: "Secure Document Management", desc: "SharePoint and OneDrive configured for secure sharing of plans, contracts and compliance files.", href: "/cloud-solutions/microsoft-365" },
  { title: "Microsoft 365 for Teams", desc: "Email, Teams, and collaboration tools set up for sales, admin and project teams.", href: "/cloud-solutions/microsoft-365" },
  { title: "On-Site Wi-Fi & Connectivity", desc: "Fast, reliable Wi-Fi for show homes, sales suites and temporary site offices.", href: "/network-solutions" },
  { title: "CCTV & Access Control", desc: "Integrated security camera systems and access control for development sites and offices.", href: "/it-infrastructure" },
  { title: "Cloud Backup & Recovery", desc: "Automated backup for critical project files, financial data and client records.", href: "/cloud-solutions" },
];

export default function PropertyDevelopmentPage() {
  return (
    <PageTransition>
      <main style={{ background: "#070B14", overflowX: "hidden" }}>
        <Header />
        <Breadcrumb
          items={[
            { label: "Home", href: "/" },
            { label: "Industries", href: "/industries" },
            { label: "Property Development & Real Estate" },
          ]}
        />

        {/* Hero */}
        <section className="relative min-h-[320px] md:min-h-[480px] flex items-center justify-center py-32 overflow-hidden">
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #070B14 0%, #0B1220 40%, #0f1f3d 70%, #070B14 100%)" }} />
          <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full blob-1" style={{ background: "radial-gradient(circle, rgba(37,99,235,0.15) 0%, transparent 70%)" }} />
          <div className="absolute bottom-1/4 left-1/4 w-80 h-80 rounded-full blob-2" style={{ background: "radial-gradient(circle, rgba(34,211,238,0.08) 0%, transparent 70%)" }} />
          <div className="relative max-w-container mx-auto px-4 sm:px-6 text-center">
            <FadeInView delay={0}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-pill mb-8" style={{ background: "rgba(37,99,235,0.12)", border: "1px solid rgba(59,130,246,0.25)" }}>
                <span className="w-2 h-2 rounded-full bg-blue-400" />
                <span className="text-[11px] font-body font-600 uppercase tracking-widest gradient-text">Industry — Property & Real Estate</span>
              </div>
            </FadeInView>
            <FadeInView delay={0.15}>
              <h1 className="font-heading font-800 text-display-md md:text-display mb-6" style={{ color: "#F8FAFC", letterSpacing: "-0.03em", lineHeight: "1.05" }}>
                IT That Keeps Your<br />
                <span className="gradient-text">Properties Connected</span>
              </h1>
            </FadeInView>
            <FadeInView delay={0.3}>
              <p className="text-body-lg leading-[1.75] max-w-3xl mx-auto mb-10" style={{ color: "#94A3B8" }}>
                From development sites to sales offices — reliable infrastructure, secure document sharing, and seamless connectivity across every location.
              </p>
            </FadeInView>
            <FadeInView delay={0.45}>
              <Link href="/book" className="px-6 sm:px-8 py-3.5 sm:py-4 text-[15px] font-heading font-600 rounded-pill btn-primary">Book a Free IT Audit</Link>
            </FadeInView>
          </div>
        </section>

        {/* Stat Bar */}
        <section className="py-10" style={{ background: "#0B1220" }}>
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-center gap-3 text-center">
              <span className="text-[15px] font-body font-500" style={{ color: "#94A3B8" }}>
                Trusted by NZ property firms managing <strong style={{ color: "#60A5FA" }}>10+ active sites</strong>
              </span>
            </div>
          </div>
        </section>

        {/* Challenges */}
        <section className="py-20 lg:py-28">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView className="text-center mb-16">
              <div className="section-sep mx-auto mb-5" />
              <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Challenges We Solve</h2>
              <p className="text-body-lg max-w-2xl mx-auto" style={{ color: "#94A3B8" }}>
                Property development IT is complex — multiple locations, temporary setups, and strict compliance requirements. We handle all of it.
              </p>
            </FadeInView>
            {/* Desktop */}
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch" staggerDelay={0.08}>
              {challenges.map((c) => (
                <div key={c.title} className="rounded-card-lg p-6 flex flex-col" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: "rgba(59,130,246,0.1)", color: "#60A5FA" }}>
                    {c.icon}
                  </div>
                  <h3 className="font-heading font-700 text-[20px] mb-3" style={{ color: "#F8FAFC" }}>{c.title}</h3>
                  <p className="text-[14px] font-body leading-[1.75]" style={{ color: "#94A3B8" }}>{c.desc}</p>
                </div>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* Services */}
        <section className="py-20 lg:py-28" style={{ background: "rgba(255,255,255,0.02)" }}>
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView className="text-center mb-16">
              <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>What We Provide</h2>
              <p className="text-body-lg max-w-2xl mx-auto" style={{ color: "#94A3B8" }}>
                End-to-end IT services designed for the property and real estate industry.
              </p>
            </FadeInView>
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch" staggerDelay={0.08}>
              {services.map((s) => (
                <Link key={s.title} href={s.href} className="group rounded-card-lg p-6 flex flex-col transition-all duration-300 hover:scale-[1.02] hover:border-blue-500/30" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <h3 className="font-heading font-700 text-[20px] mb-3" style={{ color: "#F8FAFC" }}>{s.title}</h3>
                  <p className="text-[14px] font-body leading-[1.75] mb-4" style={{ color: "#94A3B8" }}>{s.desc}</p>
                  <div className="flex items-center gap-2 text-[14px] font-body font-medium mt-auto" style={{ color: "#60A5FA" }}>
                    Learn more
                    <svg className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </div>
                </Link>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView scale={0.95}>
              <div className="rounded-card-lg p-6 sm:p-10 lg:p-16 text-center" style={{ background: "rgba(37,99,235,0.08)", border: "1px solid rgba(59,130,246,0.2)" }}>
                <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Ready to connect your sites?</h2>
                <p className="text-body-lg mb-8 leading-[1.75]" style={{ color: "#94A3B8" }}>Let&apos;s discuss your property IT requirements and build a reliable technology foundation across all your locations.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link href="/book" className="px-6 sm:px-8 py-3.5 sm:py-4 rounded-pill font-heading font-600 btn-primary">Book a Free IT Audit</Link>
                  <Link href="/services" className="px-6 sm:px-8 py-3.5 sm:py-4 rounded-pill font-heading font-500 btn-secondary">View All Services</Link>
                </div>
              </div>
            </FadeInView>
          </div>
        </section>

        <Footer />
      </main>
    </PageTransition>
  );
}
